import {
  IAuthDataSource,
  IAuthorizationService,
  METRIKA_PRODUCT_CODE,
  RegistrationGoal,
  UNEXPECTED_ERROR_KEY,
} from '../../../../infrastructure/types/features/auth/auth-types';
import type { IMetrikaService } from '../../../../../shared/infrastructure/types/common/metrika-types';
import {
  IAppLoaderStorage,
  StorageKey,
} from '../../../../infrastructure/types/utils/storage-types';
import {
  ResponseHttpStatusCode,
  ResponseStatus,
} from '../../../../../shared/infrastructure/types/api/api-types';
import {
  AccountResponse,
  UserResponse,
} from '../../../../../shared/infrastructure/types/common/common-contracts';
import {
  LoginData,
  LoginErrorDataResponse,
  LoginRequest,
  ProductResponse,
} from '../../../../infrastructure/types/features/auth/auth-contracts';
import { Loader } from '../../../../infrastructure/utils/auth/loader';
import { ipLookup } from '../../../../infrastructure/utils/ip-lookup';
import { makeAutoObservable, runInAction } from 'mobx';
import { LoginManager } from './managers/LoginManager';
import { StatusManager } from './managers/StatusManager';
import { LoginNotificator } from './notificators/LoginNotificator';
import {
  ExternalNotification,
  LocalNotification,
  Product,
} from '../../../../../shared/infrastructure/types/common/common-types';
import { RegisterManager } from './managers/RegisterManager';
import { RestorePasswordManager } from './managers/RestorePasswordManager';
import { InvitationManager } from './managers/InvitationManager';
import { YandexCloudManager } from './managers/YandexCloudManager';

class AuthorizationModule {
  currentUser: UserResponse | undefined;
  account: AccountResponse | undefined;
  productConfig: ProductResponse | undefined;
  productConfigLoaded = false;
  temporarySignInErrorKey: string | undefined = undefined;
  private sessionProbed = false;
  private temporarySignInStarted = false;

  loginManager: LoginManager;
  registerManager: RegisterManager;
  restorePasswordManager: RestorePasswordManager;
  invitationManager: InvitationManager;
  yandexCloudManager: YandexCloudManager;
  statusManager: StatusManager;
  notificator: LoginNotificator;

  private expiresIn = 0;

  constructor(
    private authService: IAuthorizationService,
    private appStorage: IAppLoaderStorage,
    private datasource: IAuthDataSource,
    private reloadPage: () => void,
    private navigateToLogin: () => void,
    private navigateToRegister: () => void,
    private navigateToMain: () => void,
    private navigateToPath: (path: RoutePath) => void,
    private releaseNavigation: () => void,
    private readonly metrikaService: IMetrikaService<RegistrationGoal>,
  ) {
    makeAutoObservable(this, {});

    this.statusManager = new StatusManager();
    this.notificator = new LoginNotificator();
    this.yandexCloudManager = new YandexCloudManager(this.authService);
    this.loginManager = new LoginManager(
      this.datasource,
      this.yandexCloudManager,
      () => this.statusManager.status.loginForm,
      () => this.statusManager.status.loginLoadingStatus,
      status => this.statusManager.setLoginFormStatus(status),
      status => this.statusManager.setLoginLoadingStatus(status),
      (notification: ExternalNotification) => this.notificator.pushExternal(notification),
      () => this.navigateToMain(),
      path => this.navigateToPath(path),
      this.metrikaService,
    );
    this.registerManager = new RegisterManager(
      this.datasource,
      this.yandexCloudManager,
      this.appStorage,
      () => this.statusManager.status.registerFormStatus,
      status => this.statusManager.setRegisterFormStatus(status),
      () => this.statusManager.status.enterPhoneLoading,
      status => this.statusManager.setEnterPhoneLoadingStatus(status),
      (email, password) => {
        this.loginManager.startRegistrationFlow(
          this.registerManager.trackingManager.registrationStartPage,
        );
        this.loginManager.setLogin(email);
        this.loginManager.setPassword(password);
        this.loginManager.submitLogin();
      },
      () => this.statusManager.status.verificationPrepare,
      status => this.statusManager.setVerificationPrepareStatus(status),
      this.metrikaService,
    );
    this.restorePasswordManager = new RestorePasswordManager(
      this.datasource,
      () => this.statusManager.status.recoveryFormStatus,
      status => this.statusManager.setRecoveryFormStatus(status),
      () => this.navigateToLogin(),
    );
    this.invitationManager = new InvitationManager(
      this.datasource,
      () => this.statusManager.status.recoveryFormStatus,
      status => this.statusManager.setRecoveryFormStatus(status),
      (email, password) => {
        this.loginManager.setLogin(email);
        this.loginManager.setPassword(password);
        this.loginManager.submitLogin();
      },
    );
  }

  trySessionLogin() {
    if (this.sessionProbed) {
      return;
    }
    this.sessionProbed = true;
    this.showRejectReasonNotification();

    if (this.yandexCloudManager.hasTransition) {
      return;
    }

    this.datasource.getUsersMe().then(response => {
      if (response.status === ResponseStatus.SUCCESS) {
        this.navigateToMain();
      }
    });
  }

  tryLogin() {
    this.sessionProbed = true;
    this.authService.saveHashRoute();
    this.authService.applyCrossDomainCookieFromQuery();

    this.loadProductConfig().then(() => {
      this.showRejectReasonNotification();

      if (this.yandexCloudManager.hasTransition) {
        this.navigateToLogin();
        return;
      }

      const accessToken = this.authService.getAccessToken();

      if (accessToken) {
        this.loginWithAccessToken(accessToken);
        return;
      }

      this.fetchUsersMe();
    });
  }

  private fetchUsersMe() {
    this.datasource.getUsersMe().then(response => {
      if (response.status === ResponseStatus.SUCCESS) {
        const { id, account_id, auth } = response.result;

        this.handleLogin({
          userId: id,
          accountId: account_id,
          token: auth.access_token,
          expiresIn: auth.expires_in,
        });
      } else if (
        response.status === ResponseStatus.SERVICE_ERROR &&
        response.error.httpStatusCode === ResponseHttpStatusCode.UNAUTHORIZED
      ) {
        this.handleAuthError(response.error);
        this.showRejectReasonNotification();
        this.navigateToLogin();
      } else {
        this.tryExtractUserInfo();
      }
    });
  }

  private loginWithAccessToken(accessToken: string) {
    ipLookup.getIP().then(ip => {
      const request: LoginRequest = {
        access_token: accessToken as AccessTokenBody,
        user: {
          ip_address: (ip || 'unknown') as IpAddress,
          tz_offset: new Date().getTimezoneOffset() as TimezoneOffset,
        },
      };

      this.datasource.login(request).then(response => {
        if (response.status !== ResponseStatus.SUCCESS) {
          this.navigateToLogin();
          return;
        }

        this.datasource
          .executeAuthCallback(response.result.callback_data)
          .then(callbackResponse => {
            if (callbackResponse.status === ResponseStatus.SUCCESS) {
              this.authService.clearURLAccessToken();
              this.fetchUsersMe();
              return;
            }

            this.datasource.logout().finally(() => this.navigateToLogin());
          });
      });
    });
  }

  tryTemporarySignIn(token: TemporaryAuthToken) {
    if (this.temporarySignInStarted) {
      return;
    }
    this.temporarySignInStarted = true;
    this.temporarySignInErrorKey = undefined;

    this.datasource.temporarySignIn({ token }).then(response => {
      if (response.status !== ResponseStatus.SUCCESS) {
        this.appStorage.clearEntry(StorageKey.SKIP_CONNECT_TO_WEBRTC);
        runInAction(() => {
          this.temporarySignInErrorKey =
            (response.status === ResponseStatus.SERVICE_ERROR &&
              (response.error.errorData as LoginErrorDataResponse)?.error) ||
            UNEXPECTED_ERROR_KEY;
        });
        return;
      }

      this.appStorage.addEntry(StorageKey.SKIP_CONNECT_TO_WEBRTC, true);
      this.datasource.executeAuthCallback(response.result.callback_data).then(callbackResponse => {
        if (callbackResponse.status === ResponseStatus.SUCCESS) {
          this.fetchUsersMe();
          return;
        }

        this.appStorage.clearEntry(StorageKey.SKIP_CONNECT_TO_WEBRTC);
        this.datasource.logout().finally(() => this.navigateToLogin());
      });
    });
  }

  private showRejectReasonNotification() {
    const rejectReason = this.authService.getRejectReason();
    if (rejectReason === 'mismatch_workspace_token') {
      this.loginManager.setPageError('login.error.mismatchWorkspaceToken');
    }
  }

  logout() {
    this.datasource.logout().finally(() => {
      this.authService.clearTimeouts();
      this.clearAuthInfo();
      this.navigateToLogin();
    });
  }

  setupAuthInfo(user: UserResponse) {
    this.currentUser = user;
    this.authService.setupAuthorization(user.account_id);
  }

  setupAccountInfo(account: AccountResponse) {
    this.account = account;
  }

  async loadProductConfig(): Promise<void> {
    const host = window.location.host;

    const response = await this.datasource.getProduct(host);

    if (response.status === ResponseStatus.SUCCESS) {
      runInAction(() => {
        this.productConfig = response.result;
        window.product = response.result as unknown as Product;

        if (response.result.name) {
          document.title = response.result.name;
        }
        if (response.result.favicon) {
          const favicon = document.querySelector("link[rel='shortcut icon']") as HTMLLinkElement;
          if (favicon) {
            favicon.href = response.result.favicon;
          }
        }
        if (response.result.code === METRIKA_PRODUCT_CODE) {
          this.metrikaService.init({ roistatId: this.registerManager.trackingManager.roistatId });
        }
      });
    }

    runInAction(() => {
      this.productConfigLoaded = true;
    });
  }

  private handleLogin({ userId, accountId, token, expiresIn }: LoginData) {
    this.authService.clearURLAccessToken();
    this.authService.clearTimeouts();
    this.expiresIn = expiresIn;
    this.authService.setAuthToken(token);

    this.datasource.getShard(accountId).then(response => {
      if (response.status !== ResponseStatus.SUCCESS) {
        this.navigateToLogin();
        return;
      }

      const { entrypoints, failure_entrypoints } = response.result;
      const servers = Array.from(new Set([...entrypoints, ...failure_entrypoints]));

      this.authService.setAppServers(servers);

      let manifestPath = this.authService.getManifestPath();

      const params = new URLSearchParams(location.search);
      const frontendV3Flag = localStorage.getItem(`frontend-v3-${accountId}`);
      if (
        (frontendV3Flag !== null && frontendV3Flag !== '0') ||
        params.get('new_frontend_embed') === '1'
      ) {
        manifestPath = '/tube-assets/manifest.json';
      }

      const loader = new Loader(servers, manifestPath, this.datasource);

      loader
        .load(manifest => this.authService.setLocales(manifest.locales))
        .then(() => {
          this.releaseNavigation();
          this.authService.startAuthCheckInterval(() => this.checkAuth(userId));
          this.authService.startTokenRefreshInterval(this.expiresIn, () => this.fetchAuthToken());
        })
        .catch(() => {
          this.navigateToLogin();
        });
    });
  }

  private checkAuth(userId: UserID) {
    this.datasource.getUsersMe().then(response => {
      if (response.status === ResponseStatus.SUCCESS) {
        if (response.result.id !== userId) {
          this.authService.setRejectReason('user_changed');
          this.reloadPage();
          return;
        }

        this.authService.setAuthToken(response.result.auth.access_token);
        this.authService.saveUserInfo({
          userId: response.result.id,
          accountId: response.result.account_id,
          token: response.result.auth.access_token,
          expiresIn: response.result.auth.expires_in,
          receivedIn: Date.now(),
        });
      } else if (
        response.status === ResponseStatus.SERVICE_ERROR &&
        response.error.httpStatusCode === ResponseHttpStatusCode.UNAUTHORIZED
      ) {
        this.handleAuthError(response.error);
        this.reloadPage();
      }
    });
  }

  private fetchAuthToken() {
    this.datasource.refreshToken().then(response => {
      if (response.status === ResponseStatus.SUCCESS) {
        this.authService.setAuthToken(response.result.access_token);
        this.authService.startTokenRefreshInterval(this.expiresIn, () => this.fetchAuthToken());
      } else if (
        response.status === ResponseStatus.SERVICE_ERROR &&
        response.error.httpStatusCode === ResponseHttpStatusCode.UNAUTHORIZED
      ) {
        this.handleAuthError(response.error);
      } else {
        this.authService.startTokenRefreshTimeout(() => this.fetchAuthToken());
      }
    });
  }

  private tryExtractUserInfo() {
    const userInfo = this.authService.loadUserInfo();

    if (!userInfo) {
      this.navigateToLogin();
      return;
    }

    const isExpired = Date.now() > userInfo.receivedIn + userInfo.expiresIn * 1000;

    if (isExpired) {
      this.navigateToLogin();
      return;
    }

    this.handleLogin(userInfo);
  }

  openLoginPage() {
    this.navigateToLogin();
  }

  openRegisterPage() {
    this.navigateToRegister();
  }

  private handleAuthError(error?: { headers?: Record<string, string> }) {
    this.authService.clearTimeouts();
    const rejectReason = error?.headers?.['x-reject-reason'];
    if (rejectReason) {
      this.authService.setRejectReason(rejectReason);
    }
    this.clearAuthInfo();
  }

  private clearAuthInfo() {
    this.authService.clearAuthorization();
  }
}

export { AuthorizationModule };
